import { getGatewayBaseUrl } from '../../api/client.ts';
import { localizeApiError } from '../../api/errorMessages.ts';
import { useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, cx, Icon } from '../../ui/index.ts';

/**
 * Three-card endpoint strip on `/docs`: Anthropic Base URL (gateway
 * origin, chip 不带 `/v1`), OpenAI Base URL (origin + `/v1`, chip 必须带
 * `/v1`), and the Authorization header shape (chip 完整密钥只显示一次).
 *
 * These cards are ENVIRONMENT-DERIVED local content. Base URLs vary
 * per env; the served document is deliberately env-agnostic (placeholders
 * only, no hostname). So this strip is a permanent local component of
 * the docs page, not part of the served document, and it will SURVIVE
 * #152's handwritten-page deletion.
 *
 * Values come from `getGatewayBaseUrl()` — the same runtime gateway-origin
 * source the handwritten page used. Copy and chip labels reuse the
 * existing `docs.fact.*` i18n keys; do not re-translate.
 */
export function EndpointCards(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const anthropicBase = getGatewayBaseUrl();
  const openaiBase = `${anthropicBase}/v1`;

  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('docs.toast.copied'));
    } catch (err) {
      toast.error(localizeApiError(err, t));
    }
  }

  const onCopy = (text: string) => () => {
    void copy(text);
  };

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3" data-testid="docs-fact-cards">
      <FactCard
        title={t('docs.fact.anthropic.title')}
        note={t('docs.fact.anthropic.note')}
        noteTone="warn"
        value={anthropicBase}
        copyLabel={t('docs.fact.copy')}
        onCopy={onCopy(anthropicBase)}
        testId="docs-fact-anthropic"
      />
      <FactCard
        title={t('docs.fact.openai.title')}
        note={t('docs.fact.openai.note')}
        noteTone="accent"
        value={openaiBase}
        copyLabel={t('docs.fact.copy')}
        onCopy={onCopy(openaiBase)}
        testId="docs-fact-openai"
      />
      {/*
        Header shape only. The console does not print a key prefix here:
        the real value is shown once, at creation time, on /keys.
      */}
      <FactCard
        title={t('docs.fact.auth.title')}
        note={t('docs.fact.auth.note')}
        value="Authorization: Bearer sk-…"
        testId="docs-fact-auth"
      />
    </div>
  );
}

function FactCard({
  title,
  note,
  noteTone = 'neutral',
  value,
  copyLabel,
  onCopy,
  testId,
}: {
  readonly title: string;
  readonly note: string;
  readonly noteTone?: 'neutral' | 'warn' | 'accent';
  readonly value: string;
  readonly copyLabel?: string;
  readonly onCopy?: () => void;
  readonly testId?: string;
}): JSX.Element {
  const noteClass =
    noteTone === 'warn'
      ? 'border-warn-line text-warn'
      : noteTone === 'accent'
        ? 'border-forest-line text-forest-deep'
        : 'border-line text-ink-low';
  return (
    <article
      data-testid={testId}
      className="min-w-0 rounded-lg border border-line-soft bg-card p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="m-0 text-body font-medium text-ink">{title}</h2>
        <span
          className={cx(
            'whitespace-nowrap rounded-full border px-2 py-0.5 text-caption',
            noteClass,
          )}
        >
          {note}
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="m-0 min-w-0 overflow-x-auto whitespace-nowrap font-mono text-body text-ink-mid">
          {value}
        </p>
        {onCopy !== undefined && copyLabel !== undefined ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onCopy}
            data-testid={testId !== undefined ? `${testId}-copy` : undefined}
          >
            <Icon name="content_copy" size="xs" />
            {copyLabel}
          </Button>
        ) : null}
      </div>
    </article>
  );
}
